declare global {
  interface Window {
    optimizely: any;
    optimizelyEdge: any;
    heap: any;
  }
}
type ExpData = { [expName: string]: string };
type CohortData = ExpData[];

const getObjectValues = (obj): Array<any> => {
  const values = [];
  for (const prop in obj) {
    if (obj.hasOwnProperty(prop)) {
      values.push(obj[prop]);
    }
  }
  return values;
};

const optimizely = {
  isLoaded: (): boolean => !!(window.optimizely && window.optimizely.get),
  getCohortData: (): CohortData => {
    if (!optimizely.isLoaded()) {
      return [];
    }

    const activeCampaigns = getObjectValues(
      window.optimizely.get('state').getCampaignStates() || {},
    ).filter((campaign: any) => campaign.isActive);

    const cohortData = activeCampaigns.map((campaign: any): ExpData => {
      const key =
        campaign.experiment && campaign.experiment.name ? campaign.experiment.name : 'none';
      const value =
        campaign.variation && campaign.variation.name ? campaign.variation.name : 'none';

      return {
        [key]: value,
      };
    });

    return cohortData;
  },
};
const optimizelyEdge = {
  isLoaded: (): boolean => !!(window.optimizelyEdge && window.optimizelyEdge.get),
  getCohortData: (): CohortData => {
    if (!optimizelyEdge.isLoaded()) {
      return [];
    }

    const activeCampaigns = getObjectValues(
      window.optimizelyEdge.get('state').getActiveExperiments() || {},
    );

    const cohortData = activeCampaigns.map((campaign: any): ExpData => {
      const key = campaign.name ? campaign.name : 'none';
      const value =
        campaign.variation && campaign.variation.name ? campaign.variation.name : 'none';

      return {
        [key]: value,
      };
    });

    return cohortData;
  },
};
const getCohortData = (): CohortData => {
  if (optimizely.isLoaded()) {
    return optimizely.getCohortData();
  }
  if (optimizelyEdge.isLoaded()) {
    return optimizelyEdge.getCohortData();
  }
  return [];
};

const trackCohortData = () => {
  // Precise stub guard: heap must exist AND expose addUserProperties. The Heap
  // snippet queues calls against the stub until heap-*.js loads, so this is
  // safe; checking the method directly avoids a method-less-stub throwing.
  if (!(window.heap && typeof window.heap.addUserProperties === 'function')) return;
  const experiments = getCohortData();
  for (const exp of experiments) {
    window.heap.addUserProperties(exp);
  }
};

let cohortTrackingRegistered = false;

/* PUB-8063: Optimizely cohort data was never reaching Heap because
   trackCohortData() ran once, synchronously, on mount — long before
   Optimizely's CDN snippet (un-gated by Ketch's slow tag orchestrator) had
   initialized window.optimizely.get('state'), so getCohortData() always
   returned []. Instead, register a campaignDecided lifecycle listener on the
   window.optimizely queue (Optimizely's documented addListener API:
   https://docs.developers.optimizely.com/web-experimentation/reference/add-listener).
   The handler fires WHEN Optimizely actually decides a variation, removing the
   timing dependency. Pushing to the queue before the snippet loads is the same
   pattern already used in optimizely_tracking.js. */
const initOptimizelyCohortTracking = () => {
  // Optimizely's snippet creates this array, but be defensive in case this runs
  // before it (Ketch-gated load / tests).
  window.optimizely = window.optimizely || [];

  // Guard against React 18 strict-mode double-invoking the mount effect.
  if (cohortTrackingRegistered) return;
  cohortTrackingRegistered = true;

  window.optimizely.push({
    type: 'addListener',
    filter: { type: 'lifecycle', name: 'campaignDecided' },
    // Re-read full state on each decision; addUserProperties is idempotent.
    handler: () => trackCohortData(),
    token: 'ttam-heap-cohort',
  });

  // Safety net: Optimizely can decide before the listener registers (and
  // campaignDecided may not replay to a late listener). Since PUB-8150 the Heap
  // queue stub is always present (tags/heap.html defines it un-gated), so this
  // idempotent one-shot reliably queues campaigns already decided by now; the
  // queued calls drain to Heap once analytics consent loads the library.
  trackCohortData();
};

// test-only: reset the module-level registration flag between tests
const _resetCohortTrackingForTests = () => {
  cohortTrackingRegistered = false;
};

const identify = (email: string) => {
  if (!email) return;
  window.heap && window.heap.identify(email);
};

export { trackCohortData, initOptimizelyCohortTracking, identify, _resetCohortTrackingForTests };
